/**
 * @file ActivityLogPage.tsx
 * @brief Activity log: who deleted or changed what, and when.
 *
 * @details
 * DES 05-10-2026, the cheapest basic log. It lists deletes, member / seat /
 * feature-access changes, and Super Admin actions on an account. Logins live
 * in each user's Login history; shares and billing have their own pages.
 *
 * `scope="company"` is the company admin's own company, with Super Admin
 * actions shown as "VERBLIZR Support". `scope="platform"` is the Super Admin
 * view: every company, real names, and a company filter.
 *
 * @author DES
 * @date 05-10-2026
 */

import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/data/DataTable";
import { Button } from "@/components/ui/Button";
import { formatDateTime } from "@/shared/utils/formatters";
import {
  GridBand,
  GridBlock,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { CompanyPicker } from "@/features/companies/components/CompanyPicker";
import { describeModelTrainingChange } from "@/features/model-training/modelTraining";
import { describeLinkChange } from "@/features/link-settings/linkSettings";
import { useActivityLog, type ActivityEvent, type ActivityScope } from "./api";

const ROLE_LABEL: Record<string, string> = {
  superadmin: "Super Admin",
  company_admin: "Company admin",
  admin: "Company admin",
  user: "Member",
};

function humanize(key: string): string {
  const s = key.replace(/[_-]+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Feature keys whose plain humanize() reads wrong ("Ai connectors"). */
const FEATURE_LABELS: Record<string, string> = {
  ai_connectors: "AI assistant connectors",
  asana: "Asana",
  zapier: "Zapier",
  make: "Make",
};
const featureLabel = (k: string) => FEATURE_LABELS[k] ?? humanize(k);

/** ADR-051 switch keys, as the Data & sharing page names them. */
const DATA_CONTROL_LABELS: Record<string, string> = {
  shareInsideOnly: "Only share inside the company",
  adminsToo: "Admins too",
  allowExport: "Allow PDF and text export",
};

/** One sentence for the event, without the actor or the target name. */
function describeActivity(e: ActivityEvent): string {
  const d = (e.details ?? {}) as Record<string, unknown>;
  switch (e.action) {
    case "session.deleted":
      return "Deleted an interpretation session";
    case "session.audio_deleted":
      return "Deleted a session's audio";
    case "session.files_deleted":
      return "Deleted a session's transcript and invoice";
    case "meeting.deleted":
      return "Deleted a meeting";
    // Company data controls (ADR-051, 21-10-2026).
    case "company.data_controls_changed": {
      const changes = (d.changes ?? {}) as Record<string, { to?: unknown } | undefined>;
      const parts = Object.entries(changes).map(([k, v]) => {
        const label = DATA_CONTROL_LABELS[k] ?? humanize(k);
        return `${label} ${v?.to === true ? "on" : v?.to === false ? "off" : "changed"}`;
      });
      return parts.length ? `Data & sharing: ${parts.join(", ")}` : "Changed Data & sharing";
    }
    case "company.data_controls_seen":
      return "Saw the Data & sharing notice";
    case "share.blocked_external":
      return d.atRecording ? "Share outside the company blocked at recording" : "Share outside the company blocked";
    case "share.external_by_admin":
      return "Shared outside the company (admin)";
    case "share.external_removed_all": {
      const m = Number(d.meetings ?? 0);
      const n = Number(d.notes ?? 0);
      return `Removed all external shares (${m} meeting${m === 1 ? "" : "s"}, ${n} note${n === 1 ? "" : "s"})`;
    }
    case "export.blocked":
      return "Export blocked (exports are off)";
    case "connector.blocked":
      return d.connector ? `${humanize(String(d.connector))} blocked (not allowed for the company)` : "Connector blocked";
    // Sharing v2 (21-10-2026): a company admin took someone off a member's
    // meeting from "Who has access". Target = the meeting title.
    case "share.removed_by_admin":
      // `removed` is the user id or email; only an email reads as a name.
      return String(d.removed ?? "").includes("@")
        ? `Removed a share (${String(d.removed)})`
        : "Removed a share";
    // B45 (01-11-2026): an invitee's access taken back from "Who has access"
    // (owner or company admin). Target = the meeting title.
    case "invite.access_removed":
    case "invite.access_removed_by_admin": {
      const who = String(d.removedName ?? d.removedEmail ?? "");
      const by = e.action === "invite.access_removed_by_admin" ? " (admin)" : "";
      return who ? `Removed ${who}'s access${by}` : `Removed an invitee's access${by}`;
    }
    // B48 (02-11-2026): editing a meeting during and after it. Target =
    // the meeting title.
    case "meeting.edited": {
      const changed = Array.isArray(d.changed) ? (d.changed as string[]) : [];
      const words: Record<string, string> = {
        title: "title",
        participants: "people",
        agenda: "agenda",
        time: "time",
        languages: "languages",
      };
      const what = changed.map((c) => words[c] ?? humanize(c)).join(", ");
      const when = d.phase === "during" ? " during the meeting" : d.phase === "after" ? " after the meeting" : "";
      return what ? `Edited the meeting's ${what}${when}` : `Edited a meeting${when}`;
    }
    case "meeting.renamed":
      return d.titleFrom ? `Renamed a meeting (was “${String(d.titleFrom)}”)` : "Renamed a meeting";
    case "todo.edited": {
      const parts = [d.textChanged ? "wording" : "", d.dueDateChanged ? "due date" : ""].filter(Boolean);
      return parts.length ? `Edited a to-do's ${parts.join(" and ")}` : "Edited a to-do";
    }
    case "meeting.guest_email_added":
      return "Added a guest's email";
    case "member.added":
      return "Added a member";
    case "member.removed":
      return "Removed a member";
    case "member.data_exported":
      return "Downloaded a member's data";
    case "member.status_changed":
      return d.status === "suspended"
        ? "Suspended a member"
        : d.status === "active"
          ? "Reactivated a member"
          : `Set a member's status to ${String(d.status ?? "unknown")}`;
    case "member.updated":
      return d.role ? `Updated a member (role: ${humanize(String(d.role))})` : "Updated a member";
    case "member.password_reset":
      return "Reset a member's password";
    case "member.card_assigned":
      return "Assigned a company card to a member";
    case "member.card_cleared":
      return "Cleared a member's company card";
    case "seat.assigned":
      return "Assigned an Enterprise seat";
    case "seat.revoked":
      return "Removed an Enterprise seat";
    case "company.features_changed": {
      const changes = (d.changes ?? {}) as Record<string, unknown>;
      const on = Object.keys(changes).filter((k) => changes[k] === true).map(featureLabel);
      const off = Object.keys(changes).filter((k) => changes[k] === false).map(featureLabel);
      const parts = [
        on.length ? `turned on ${on.join(", ")}` : "",
        off.length ? `turned off ${off.join(", ")}` : "",
      ].filter(Boolean);
      return parts.length ? humanize(parts.join("; ")) : "Changed feature access";
    }
    // ADR-048 (20-10-2026): the company switch for model improvement.
    case "company.model_training_changed":
      return describeModelTrainingChange(d);
    // MCP (06-10-2026): someone let an AI app read their meetings, or stopped it.
    case "ai_app.connected":
      return d.app ? `Connected ${String(d.app)} to read their meetings` : "Connected an AI app";
    case "ai_app.disconnected":
      return "Disconnected an AI app";
    // Z1 (27-10-2026): a Zapier connection made or ended.
    case "zapier.connected":
      return "Connected Zapier";
    case "zapier.disconnected":
      return "Disconnected Zapier";
    // M1 (29-10-2026): the same for Make.
    case "make.connected":
      return "Connected Make";
    case "make.disconnected":
      return "Disconnected Make";
    case "company.created":
      return "Created the company";
    case "company.updated":
      return "Updated company details";
    case "company.deleted":
      return "Deleted the company";
    case "company.suspended":
      return "Suspended the company";
    case "company.reactivated":
      return "Reactivated the company";
    case "plan.changed":
      return "Changed the Enterprise plan";
    // B23 (24-10-2026): Super Admin → Links & company info.
    case "setting.link_changed":
      return describeLinkChange(d);
    case "plan.status_changed":
      return `Set the Enterprise plan to ${String(d.status ?? "unknown")}`;
    default:
      return humanize(e.action.replace(".", " "));
  }
}

export function ActivityLogPage({ scope }: { scope: ActivityScope }) {
  const [companyId, setCompanyId] = useState<string | null>(null);
  const { data, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useActivityLog(scope, companyId);

  const rows = useMemo(() => data?.pages.flatMap((p) => p.events) ?? [], [data]);

  const columns = useMemo<ColumnDef<ActivityEvent>[]>(() => {
    const cols: ColumnDef<ActivityEvent>[] = [
      {
        header: "When",
        accessorKey: "created_at",
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {formatDateTime(row.original.created_at)}
          </span>
        ),
      },
      {
        header: "Who",
        accessorKey: "actor_name",
        cell: ({ row }) => {
          const e = row.original;
          return (
            <div className="min-w-[160px]">
              <p className="font-medium">{e.actor_name || e.actor_email || "Unknown"}</p>
              <p className="text-xs text-muted-foreground">
                {e.actor_email && e.actor_name ? e.actor_email : ROLE_LABEL[e.actor_role ?? ""] ?? ""}
              </p>
            </div>
          );
        },
      },
      {
        header: "What",
        accessorKey: "action",
        cell: ({ row }) => {
          const e = row.original;
          return (
            <div className="min-w-[220px]">
              <p className="text-sm">{describeActivity(e)}</p>
              {e.target_label ? (
                <p className="truncate text-xs text-muted-foreground">{e.target_label}</p>
              ) : null}
            </div>
          );
        },
      },
    ];
    if (scope === "platform") {
      cols.push({
        header: "Company",
        accessorKey: "company_name",
        cell: ({ row }) => (
          <span className="text-sm">{row.original.company_name || "No company"}</span>
        ),
      });
    }
    return cols;
  }, [scope]);

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-72">
            <h2 className="text-xl font-semibold tracking-tight">Activity log</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              {scope === "platform"
                ? "Deletes, member and access changes, and Super Admin actions across every company, kept for 12 months."
                : "Who deleted or changed what in your company: meetings, sessions, members, seats and feature access. Kept for 12 months."}
            </p>
          </div>
          {scope === "platform" ? (
            <div className="flex flex-wrap items-center justify-end gap-2.5">
              <CompanyPicker
                variant="pill"
                value={companyId}
                onChange={setCompanyId}
                placeholder="All companies"
              />
            </div>
          ) : null}
        </GridBand>

        <GridCell padded={false} className="border-b-0">
          <DataTable
            variant="cell"
            columns={columns}
            data={rows}
            isLoading={isLoading}
            isFetching={isFetching && !isFetchingNextPage}
            error={error}
            onRetry={() => void refetch()}
            emptyTitle="Nothing here yet"
            emptyDescription="When someone deletes a meeting or changes a member or a setting, it shows up here."
            rowKey={(row) => row.id}
          />
          {hasNextPage ? (
            <div className="flex justify-center p-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </div>
          ) : null}
        </GridCell>
      </GridBlock>
    </div>
  );
}
